<template>
  <section
    class="my-md grid auto-rows-fr grid-cols-2 gap-x-lg px-5 mobile:grid-cols-1 desktop:px-20"
    :data-ab-id="dataAbId"
  >
    <div
      v-for="(data, i) in columnData"
      :key="i"
      class="flex flex-col"
    >
      <SharedLink :link="data.imageLink">
        <NuxtImg
          class="w-full"
          :src="data.src"
          sizes="lg:100vw 48vw"
          :srcset="data.srcset"
          :alt="data.imageAlt"
          :title="data.imageTitle"
          v-bind="performance"
        />
      </SharedLink>
      <div class="bottom-[53px] top-0 mx-xl mb-lg flex h-full flex-col items-start bg-white p-xl shadow-default mobile:m-0 mobile:p-xl tablet:m-0 tablet:p-xl">
        <h3 class="flex items-end gap-1">
          {{ data.title }}
          <KikLegalInformationDrawer
            v-if="data.enableLegalDisclaimer && data.titleOrSubtitle === 'title'"
            :legal-text="data.legalText"
            :title="data.legalTitle"
          />
        </h3>
        <p class="my-2 flex items-end gap-1 mobile:text-sm">
          {{ data.subText }}
          <KikLegalInformationDrawer
            v-if="data.enableLegalDisclaimer && data.titleOrSubtitle === 'subtitle'"
            :legal-text="data.legalText"
            :title="data.legalTitle"
          />
        </p>
        <section class="mt-auto w-full desktop:mt-xl desktop:w-auto">
          <SharedLink
            v-if="data.ctaLink && data.ctaText"
            :link="data.ctaLink"
            class="cursor-pointer rounded-md border border-neutral-900 px-8 py-sm text-center text-sm font-bold uppercase tracking-[1px] shadow-default"
            :style="{ color: `${data.ctaTextColor}`, borderColor: `${data.borderColor}`, backgroundColor: `${data.buttonColor}` }"
          >
            {{ data.ctaText }}
          </SharedLink>
        </section>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  slides?: {
    title?: string;
    titleColor?: string;
    subText?: string;
    subTextColor?: string;
    image?: string;
    imageTitle?: string;
    imageLink?: any;
    alt?: string;
    ctaLink?: any;
    intLinkParam?: string;
    ctaTextColor?: string;
    buttonColor?: string;
    borderColor?: string;
    enableLegalDisclaimer?: boolean;
    legalText?: string;
    legalTitle?: string;
    titleOrSubtitle?: string;
    aspectRatio?: string;
    mobileAspectRatio?: string;
  }[];
  intLinkParam?: string;
  imageLoadType?: HTMLImageElement['loading'];
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  slides: () => [],
  imageLoadType: 'lazy',
  intLinkParam: '',
});

const { $domain } = useNuxtApp();
const slides = toRef(props, 'slides');
const columnData = computed(() => (
  slides.value?.map(slide => ({
    ...slide,
    src: magnoliaGetters.getDefaultSrcAsset(slide.image, $domain.cmsAssetDomainPath),
    srcset: magnoliaGetters.getSrcSetImagesAsString(slide.image, $domain.cmsAssetDomainPath, slide.aspectRatio, slide.mobileAspectRatio?.[0]?.aspectRatio),
    imageTitle: magnoliaGetters.getTitleImage(slide.image, slide.imageTitle),
    imageAlt: magnoliaGetters.getAltImage(slide.image, slide.alt),
    imageLink: slide.imageLink?.[0] ? magnoliaGetters.getLinkAndName(slide.imageLink[0], $domain.cmsAssetDomainPath, slide.imageLink[0].switchable?.intLinkParam) : {},
    ctaLink: slide.ctaLink?.[0] ? magnoliaGetters.getLinkAndName(slide.ctaLink[0], $domain.cmsAssetDomainPath, slide.ctaLink[0].switchable?.intLinkParam) : {},
    ctaText: slide.ctaLink?.[0] ? slide.ctaLink?.[0].ctaText : '',
    enableLegalDisclaimer: slide.enableLegalDisclaimer,
    legalText: slide.legalText,
    legalTitle: slide.legalTitle,
    titleOrSubtitle: slide.titleOrSubtitle,
  }))
));

const performance = computed(() => ({
  loading: props.imageLoadType,
  preload: props.imageLoadType === 'eager',
  fetchpriority: props.imageLoadType === 'eager' ? 'high' : 'low',
}));
</script>
